import { z } from 'zod';
import { DashboardDirectionSchema, DashboardIntentSchema, DashboardSpecSchema, } from './dashboard.js';
/**
 * 영속 대시보드 (사용자가 "저장" 누르면 chat artifact 에서 1 row 복사) 의 zod 스키마.
 * sec/admin/sharing dialog 등 frontend 가 type-safe 하게 다룰 수 있도록 분리.
 */
export const DashboardVisibilitySchema = z.enum(['private', 'group', 'public']);
/**
 * 공유 다이얼로그의 "그룹" 셀렉터 옵션.
 * 사내 라벨로 "그룹" 이지만 실제로는 roles 테이블 — admin/roles 에서 관리되는 사용자 묶음 재활용.
 */
export const DashboardGroupOptionSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
});
export const DashboardGroupOptionListSchema = z.object({
    groups: z.array(DashboardGroupOptionSchema),
});
/** 시트별 { sqlName: 표시라벨 }. 빈 객체 default. */
export const ColumnAliasesSchema = z.record(z.string(), z.record(z.string(), z.string()));
/** 사이드바·viewer 목록에 쓰이는 요약 형태 (spec/html 미포함). */
export const DashboardSummarySchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    visibility: DashboardVisibilitySchema,
    groupId: z.string().uuid().nullable(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const DashboardSummaryListSchema = z.object({
    dashboards: z.array(DashboardSummarySchema),
});
/**
 * "공유받은 대시보드" 목록 항목 — 소유자 표시 + 그룹명 칩 렌더용 메타 추가.
 * 공유 탭 카드에서 "○○○님이 공유" / 그룹명 칩을 그리려면 owner / group 메타가 필요해서 별도 스키마.
 */
export const SharedDashboardSummarySchema = DashboardSummarySchema.extend({
    ownerUserId: z.string().uuid(),
    ownerDisplayName: z.string(),
    groupName: z.string().nullable(),
});
export const SharedDashboardSummaryListSchema = z.object({
    dashboards: z.array(SharedDashboardSummarySchema),
});
/** view 페이지에서 사용 — renderedHtml + 권한·메타 포함. */
export const DashboardDetailSchema = z.object({
    id: z.string().uuid(),
    ownerUserId: z.string().uuid(),
    ownerDisplayName: z.string(),
    title: z.string(),
    visibility: DashboardVisibilitySchema,
    groupId: z.string().uuid().nullable(),
    columnAliases: ColumnAliasesSchema,
    /** spec 자체 — 분석설계 다이얼로그가 picker 초기값으로 사용. */
    spec: DashboardSpecSchema,
    /** 서버가 spec → HTML 결정론적 렌더한 결과 (iframe srcdoc 으로 렌더). */
    renderedHtml: z.string(),
    /** owner 여부. owner 면 settings/share/delete 버튼 노출. */
    isOwner: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const SaveDashboardRequestSchema = z.object({
    /** chat artifact 의 head id. 서버가 currentVersion 의 spec 을 복사. */
    artifactId: z.string().uuid(),
    title: z.string().trim().min(1).max(120),
});
export const RenameDashboardRequestSchema = z.object({
    title: z.string().trim().min(1).max(120),
});
export const ReorderDashboardsRequestSchema = z.object({
    ids: z.array(z.string().uuid()).min(1),
});
/**
 * 공유 대상 종류.
 *  - 'user':       개별 사용자
 *  - 'user_group': 사용자가 만든 개인 그룹 (user_groups)
 *  - 'role':       관리자 RBAC 역할 (roles) — 사용자가 멤버인 역할만 허용
 */
export const SharePrincipalTypeSchema = z.enum(['user', 'user_group', 'role']);
/** 공유 대상 참조 (저장 요청 본문). */
export const SharePrincipalRefSchema = z.object({
    principalType: SharePrincipalTypeSchema,
    principalId: z.string().uuid(),
});
/** 공유 현황 1행 — 표시용 이름/부가설명 포함. */
export const SharePrincipalSchema = SharePrincipalRefSchema.extend({
    /** 사용자=displayName, 그룹/역할=name. */
    name: z.string(),
    /** 사용자=이메일, 그룹=멤버수 요약, 역할=설명 등. 없으면 null. */
    subtitle: z.string().nullable(),
});
/** GET /api/dashboards/:id/shares 응답 — 일반 접근 권한 + 명시 공유 대상 목록. */
export const DashboardSharesResponseSchema = z.object({
    /** 일반 접근 권한. 신규 저장은 private/public 만 쓰지만 구 'group' 데이터도 읽을 수 있다. */
    visibility: DashboardVisibilitySchema,
    grants: z.array(SharePrincipalSchema),
});
/**
 * 공유 저장 요청. 일반 접근 권한(비공개/전체) + 명시 공유 대상 전체 치환.
 * (구 그룹 단일 셀렉터 모델에서 Notion 식 다중 대상 모델로 교체.)
 */
export const ShareDashboardRequestSchema = z.object({
    visibility: z.enum(['private', 'public']),
    grants: z.array(SharePrincipalRefSchema).max(500).default([]),
});
export const UpdateDashboardSettingsRequestSchema = z.object({
    direction: DashboardDirectionSchema.optional(),
    intent: DashboardIntentSchema.optional(),
    columnAliases: ColumnAliasesSchema.optional(),
});
/**
 * 분석 패널 — Dashv2 §6 profileColumns + §7 auditDataQuality 의 응답.
 * "분석" 버튼 클릭 시 viewer 권한으로 saved_query 재실행 → 컬럼 프로파일 + 데이터 품질 A~D 등급.
 */
export const DashboardColumnProfileSchema = z.object({
    name: z.string(),
    dtype: z.enum(['numeric', 'categorical', 'datetime', 'boolean', 'mixed']),
    n_unique: z.number().int().min(0),
    missing_pct: z.number(),
    role_candidate: z.enum([
        'time_dimension',
        'filter_dimension',
        'breakdown_dimension',
        'identifier',
        'measure',
    ]),
    warning: z.string().optional(),
    min: z.union([z.number(), z.string()]).optional(),
    max: z.union([z.number(), z.string()]).optional(),
    mean: z.number().optional(),
    median: z.number().optional(),
    top_values: z
        .array(z.object({ value: z.string(), count: z.number().int(), pct: z.number() }))
        .optional(),
});
export const DashboardAuditIssueSchema = z.object({
    severity: z.enum(['error', 'warn', 'info']),
    column: z.string().optional(),
    message: z.string(),
    hint: z.string().optional(),
});
export const DashboardAuditReportSchema = z.object({
    score: z.number().int().min(0).max(100),
    grade: z.enum(['A', 'B', 'C', 'D']),
    metrics: z.object({
        rows: z.number().int(),
        columns: z.number().int(),
        avgMissing: z.number(),
        duplicates: z.number().int(),
        hasTimeCol: z.boolean(),
        timeGapDetected: z.boolean(),
    }),
    issues: z.array(DashboardAuditIssueSchema),
});
export const DashboardProfileResponseSchema = z.object({
    columns: z.array(DashboardColumnProfileSchema),
    audit: DashboardAuditReportSchema,
    rowCount: z.number().int(),
    truncated: z.boolean(),
});
//# sourceMappingURL=dashboards.js.map